import AsyncStorage from "@react-native-async-storage/async-storage";

export const API_URL = "/api";

async function token() {
  return AsyncStorage.getItem("auth_token");
}

export async function request(path, options = {}) {
  const authToken = await token();
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;

  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(options.headers || {})
      }
    });
  } catch (err) {
    throw new Error(
      "Unable to connect to the backend server. Please ensure the server is running."
    );
  }

  if (response.status === 204) return null;

  let data = {};
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    data = await response.json().catch(() => ({}));
  } else {
    const rawText = await response.text().catch(() => "");
    if (rawText) {
      try {
        data = JSON.parse(rawText);
      } catch (_) {
        data = { message: rawText };
      }
    }
  }

  if (!response.ok) {
    const fallbackMsg =
      response.status === 401
        ? "Invalid username or password."
        : response.status === 403
        ? "Access forbidden or account inactive."
        : response.status === 404
        ? "Service endpoint not found."
        : `Server responded with error (${response.status}).`;
    throw new Error(data.message || data.error || fallbackMsg);
  }

  return data;
}

export const api = {
  login: (username, password) =>
    request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password })
    }),

  mfaSetupVerify: (mfaToken, code) =>
    request("/auth/mfa/setup-verify", {
      method: "POST",
      body: JSON.stringify({ mfaToken, code })
    }),

  mfaVerify: (mfaToken, code) =>
    request("/auth/mfa/verify", {
      method: "POST",
      body: JSON.stringify({ mfaToken, code })
    }),

  getMfaProfile: () => request("/auth/profile/mfa"),

  reconfigureMfaInit: (password) =>
    request("/auth/profile/mfa/reconfigure-init", {
      method: "POST",
      body: JSON.stringify({ password })
    }),

  reconfigureMfaConfirm: (code) =>
    request("/auth/profile/mfa/reconfigure-confirm", {
      method: "POST",
      body: JSON.stringify({ code })
    }),

  changePassword: (current_password, password) =>
    request("/auth/profile/password", {
      method: "PUT",
      body: JSON.stringify({ current_password, password })
    }),

  dashboard: () => request("/dashboard"),

  users: (q = "", role = "", status = "") =>
    request(
      `/users?q=${encodeURIComponent(q)}&role=${encodeURIComponent(
        role
      )}&status=${encodeURIComponent(status)}`
    ),

  saveUser: (user) =>
    request(user.id ? `/users/${user.id}` : "/users", {
      method: user.id ? "PUT" : "POST",
      body: JSON.stringify(user)
    }),

  resetUserPassword: (id, password) =>
    request(`/users/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password })
    }),

  resetUserMfa: (id) =>
    request(`/users/${id}/reset-mfa`, {
      method: "POST"
    }),

  toggleUserActive: (id) =>
    request(`/users/${id}/toggle-active`, {
      method: "POST"
    }),

  deleteUser: (id) =>
    request(`/users/${id}`, {
      method: "DELETE"
    }),

  logs: (params = {}) =>
    request(`/logs?${new URLSearchParams(params).toString()}`),

  clients: (q = "", status = "") =>
    request(
      `/clients?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}`
    ),

  saveClient: (client) =>
    request(client.id ? `/clients/${client.id}` : "/clients", {
      method: client.id ? "PUT" : "POST",
      body: JSON.stringify(client)
    }),

  deleteClient: (id, permanent = false) =>
    request(`/clients/${id}${permanent ? "?permanent=1" : ""}`, {
      method: "DELETE"
    }),

  categories: () => request("/categories"),

  activeCategories: () => request("/categories/active"),

  saveMainCategory: (category) =>
    request(category.id ? `/categories/main/${category.id}` : "/categories/main", {
      method: category.id ? "PUT" : "POST",
      body: JSON.stringify(category)
    }),

  deleteMainCategory: (id) =>
    request(`/categories/main/${id}`, {
      method: "DELETE"
    }),

  saveSubCategory: (category) =>
    request(category.id ? `/categories/sub/${category.id}` : "/categories/sub", {
      method: category.id ? "PUT" : "POST",
      body: JSON.stringify(category)
    }),

  deleteSubCategory: (id) =>
    request(`/categories/sub/${id}`, {
      method: "DELETE"
    }),

  products: (
    q = "",
    categoryId = "",
    activeOnly = false,
    subCategoryId = ""
  ) =>
    request(
      `/products?q=${encodeURIComponent(q)}&category_id=${encodeURIComponent(
        categoryId
      )}&sub_category_id=${encodeURIComponent(
        subCategoryId
      )}&active=${activeOnly ? "1" : ""}`
    ),

  nextProductCode: (
    mainCategory = "",
    subCategory = "",
    mainCategoryId = "",
    subCategoryId = ""
  ) =>
    request(
      `/products/next-code?mainCategory=${encodeURIComponent(
        mainCategory
      )}&subCategory=${encodeURIComponent(
        subCategory
      )}&main_category_id=${encodeURIComponent(
        mainCategoryId
      )}&sub_category_id=${encodeURIComponent(subCategoryId)}`
    ),

  saveProduct: (product) =>
    request(product.id ? `/products/${product.id}` : "/products", {
      method: product.id ? "PUT" : "POST",
      body: JSON.stringify(product)
    }),

  importMissingProducts: (file) => {
    const body = new FormData();
    body.append("file", file);
    return request("/products/import-missing", {
      method: "POST",
      body
    });
  },

  deleteProduct: (id) =>
    request(`/products/${id}`, {
      method: "DELETE"
    }),

  settings: () => request("/settings"),

  saveSettings: (settings) =>
    request("/settings", {
      method: "PUT",
      body: JSON.stringify(settings)
    }),

  uploadLogo: (file) => {
    const body = new FormData();
    body.append("logo", file);
    return request("/settings/logo", {
      method: "POST",
      body
    });
  },

  removeLogo: () =>
    request("/settings/logo", {
      method: "DELETE"
    }),

  stampSignatureSettings: () =>
    request("/settings/quotation-stamp-signature"),

  saveStampSignatureSettings: (settings) =>
    request("/settings/quotation-stamp-signature", {
      method: "PUT",
      body: JSON.stringify(settings)
    }),

  uploadStampSignature: (file, options = {}) => {
    const body = new FormData();
    body.append("stamp_signature", file);
    body.append(
      "print_stamp_signature_on_draft_pdf",
      options.print_stamp_signature_on_draft_pdf ? "1" : "0"
    );
    body.append(
      "print_stamp_signature_on_final_pdf",
      options.print_stamp_signature_on_final_pdf ? "1" : "0"
    );

    return request("/settings/quotation-stamp-signature/upload", {
      method: "POST",
      body
    });
  },

  removeStampSignature: () =>
    request("/settings/quotation-stamp-signature", {
      method: "DELETE"
    }),

  terms: (activeOnly = false) =>
    request(`/terms?active=${activeOnly ? "1" : ""}`),

  quotations: (
    q = "",
    dateFrom = "",
    dateTo = "",
    status = ""
  ) =>
    request(
      `/quotations?q=${encodeURIComponent(
        q
      )}&date_from=${encodeURIComponent(
        dateFrom
      )}&date_to=${encodeURIComponent(
        dateTo
      )}&status=${encodeURIComponent(status)}`
    ),

  quotation: (id) =>
    request(`/quotations/${id}`),

  nextQuotationNumber: () =>
    request("/quotations/next-number"),

  saveQuotation: (quotation) =>
    request(quotation.id ? `/quotations/${quotation.id}` : "/quotations", {
      method: quotation.id ? "PUT" : "POST",
      body: JSON.stringify(quotation)
    }),

  importQuotationItems: (file) => {
    const body = new FormData();
    body.append("file", file);
    return request("/quotations/import-items", {
      method: "POST",
      body
    });
  },

  duplicateQuotation: (id) =>
    request(`/quotations/${id}/duplicate`, {
      method: "POST"
    }),

  deleteQuotation: (id) =>
    request(`/quotations/${id}`, {
      method: "DELETE"
    }),

  submitQuotation: (id) =>
    request(`/quotations/${id}/submit`, {
      method: "POST"
    }),

  approveQuotation: (id, comments = "") =>
    request(`/quotations/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ comments })
    }),

  rejectQuotation: (id, reason) =>
    request(`/quotations/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason })
    }),

  returnQuotation: (id, comments) =>
    request(`/quotations/${id}/return`, {
      method: "POST",
      body: JSON.stringify({ comments })
    }),

  pdfUrl: (id) => `${API_URL}/quotations/${id}/pdf`,

  quotationRevisions: (id) => request(`/quotations/${id}/revisions`),

  quotationRevision: (id, revId) => request(`/quotations/${id}/revisions/${revId}`),

  quotationRevisionPdfUrl: (id, revId) => `${API_URL}/quotations/${id}/revisions/${revId}/pdf`,

  invoices: (
    q = "",
    dateFrom = "",
    dateTo = "",
    status = "",
    paymentFilter = "",
    invoiceType = ""
  ) =>
    request(
      `/invoices?q=${encodeURIComponent(q)}&date_from=${encodeURIComponent(
        dateFrom
      )}&date_to=${encodeURIComponent(dateTo)}&status=${encodeURIComponent(
        status
      )}&payment_filter=${encodeURIComponent(
        paymentFilter
      )}&invoice_type=${encodeURIComponent(invoiceType)}`
    ),

  invoice: (id) => request(`/invoices/${id}`),

  nextInvoiceNumber: () => request("/invoices/next-number"),

  createInvoiceFromQuotation: (quotationId, payload = {}) =>
    request(`/invoices/from-quotation/${quotationId}`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),

  updateInvoice: (id, payload) =>
    request(`/invoices/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    }),

  issueInvoice: (id) =>
    request(`/invoices/${id}/issue`, {
      method: "POST"
    }),

  recordInvoicePayment: (id, payload) =>
    request(`/invoices/${id}/payments`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),

  cancelInvoice: (id, reason = "") =>
    request(`/invoices/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason })
    }),

  deleteInvoice: (id) =>
    request(`/invoices/${id}`, {
      method: "DELETE"
    }),

  invoicePdfUrl: (id) => `${API_URL}/invoices/${id}/pdf`
};